/**
 * c-mega-menu-slider.js
 *
 * Drives the collection tile slider inside each mega menu panel.
 *
 * WHY CLICKING AN ARROW CLOSED THE WHOLE MENU (fixed here)
 * Horizon's HeaderMenu.deactivate() only bails out when event.relatedTarget is
 * a Node inside the submenu. Pressing a <button> blurs whatever was focused —
 * usually the top-level menu link — and on a mouse click Safari and Firefox do
 * not move focus to a button at all, so relatedTarget comes through as null.
 * The vendor read that as "focus left the menu" and closed the panel, which is
 * why the arrows dismissed the menu instead of scrolling it.
 *
 * The fix is to preventDefault on mousedown/pointerdown over the controls.
 * That stops the focus change before it happens, so no blur fires and neither
 * the vendor nor our own handlers ever see focus leave. Keyboard users are
 * unaffected: Tab still focuses the buttons normally, and focus then sits
 * inside the menu, which is a case both handlers already allow.
 *
 * WHY THE ARROWS DID NOT APPEAR AT ALL (fixed earlier)
 * 1. LAYOUT — the panel renders inside Horizon's `.mega-menu__grid`, a CSS
 *    grid. A grid item is sized by its track, so the track widened its own
 *    column instead of being clipped: scrollWidth never exceeded clientWidth
 *    and measured overflow was always 0. Neutralised in the panel CSS.
 * 2. TIMING — panels are hidden until hovered, so the first measurement ran
 *    against a collapsed panel. A ResizeObserver does not fire on a visibility
 *    change, so nothing re-measured. Now re-measured on open.
 *
 * At 1440 the track is 1206px inside a 1142px column — 64px of overflow, under
 * a quarter of one tile. The arrows have to come from a real measurement.
 */
(function () {
  'use strict';

  var C_TILE_STEP = 302; // 300px tile + 2px gap

  /**
   * Neutralise the vendor grid wrapper so the track can actually be clipped.
   * Done from JS as well as CSS so the fix does not rest on :has() support.
   * @param {HTMLElement} panel
   */
  function c_tagHost(panel) {
    var c_host = panel.parentElement;
    if (c_host && c_host.classList.contains('mega-menu__grid')) {
      c_host.classList.add('c-mm-host');
      var c_section = c_host.closest('.mega-menu');
      if (c_section) c_section.classList.add('c-mm-section');
    }
  }

  /**
   * @param {HTMLElement} panel
   */
  function c_wireSlider(panel) {
    if (!panel) return;

    c_tagHost(panel);

    if (panel.dataset.cSliderWired === 'true') return;

    var c_track = panel.querySelector('[data-c-mm-track]');
    var c_controls = panel.querySelector('[data-c-mm-controls]');
    if (!c_track || !c_controls) return;

    panel.dataset.cSliderWired = 'true';

    var c_prev = panel.querySelector('[data-c-mm-prev]');
    var c_next = panel.querySelector('[data-c-mm-next]');

    /**
     * Real overflow, with a fallback.
     *
     * scrollWidth is authoritative once the panel has layout. But if the panel
     * is collapsed, or a container is still sized to content, it can read 0
     * while the tiles plainly do not fit. So the tile count is used as a
     * cross-check against the visible column width.
     */
    function c_overflow() {
      var c_measured = c_track.scrollWidth - c_track.clientWidth;
      if (c_measured > 1) return c_measured;

      var c_tiles = c_track.querySelectorAll('.c-mm__tile').length;
      var c_visible = c_track.clientWidth;
      if (c_tiles === 0 || c_visible === 0) return 0;

      var c_expected = c_tiles * C_TILE_STEP - 2; // no gap after the last tile
      var c_implied = c_expected - c_visible;
      return c_implied > 1 ? c_implied : 0;
    }

    function c_sync() {
      var c_over = c_overflow();

      if (c_over <= 1) {
        c_controls.hidden = true;
        return;
      }

      c_controls.hidden = false;

      var c_pos = c_track.scrollLeft;
      if (c_prev) c_prev.disabled = c_pos <= 1;
      if (c_next) c_next.disabled = c_pos >= c_over - 1;
    }

    /* Measure across two frames — the first catches layout, the second catches
       any transition that changes the panel's box after it opens. */
    function c_syncSoon() {
      window.requestAnimationFrame(function () {
        c_sync();
        window.requestAnimationFrame(c_sync);
      });
    }

    function c_scrollBy(direction) {
      var c_over = c_overflow();
      // When the whole overflow is less than one tile, step by the overflow
      // itself — otherwise the track slams to the end and reads as a glitch.
      var c_step = c_over < C_TILE_STEP ? c_over : C_TILE_STEP;
      c_track.scrollLeft += direction * c_step;
      c_syncSoon();
    }

    /* ----------------------------------------------------------------
     * Keep focus where it is when an arrow is pressed with a pointer.
     *
     * This is the fix for "clicking the arrows closes the mega menu".
     * preventDefault on mousedown suppresses the focus change, so the
     * top-level link never blurs and no close path is triggered. It is
     * bound on the container so both arrows are covered.
     * ---------------------------------------------------------------- */
    function c_holdFocus(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    c_controls.addEventListener('mousedown', c_holdFocus);
    c_controls.addEventListener('pointerdown', c_holdFocus);

    /* Touch devices fire neither reliably before the click, so also stop the
       click bubbling out of the panel. */
    c_controls.addEventListener('click', function (event) {
      event.stopPropagation();
    });

    if (c_prev) {
      c_prev.addEventListener('click', function () {
        c_scrollBy(-1);
      });
    }

    if (c_next) {
      c_next.addEventListener('click', function () {
        c_scrollBy(1);
      });
    }

    c_track.addEventListener('scroll', c_sync, { passive: true });

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(c_sync).observe(c_track);
    }

    window.addEventListener('resize', c_sync);

    /* ----------------------------------------------------------------
     * Re-measure when the panel actually opens.
     * A ResizeObserver does not fire on a visibility change, so without
     * this the arrows stay hidden from the initial zero-width reading.
     * ---------------------------------------------------------------- */
    var c_menu = panel.closest('header-menu');
    var c_item = panel.closest('.menu-list__list-item');

    if (c_item) {
      c_item.addEventListener('pointerenter', c_syncSoon);
      c_item.addEventListener('focusin', c_syncSoon);
    }

    if (c_menu && typeof MutationObserver === 'function') {
      new MutationObserver(c_syncSoon).observe(c_menu, {
        attributes: true,
        attributeFilter: ['aria-expanded', 'data-c-closed', 'style'],
      });
    }

    c_syncSoon();
  }

  function c_init() {
    var c_panels = document.querySelectorAll('[data-c-mega-menu-panel]');
    for (var i = 0; i < c_panels.length; i++) c_wireSlider(c_panels[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', c_init);
  } else {
    c_init();
  }

  /* Panels can be injected late by Horizon's section hydration. Scoped to the
     header rather than the whole document. */
  if (typeof MutationObserver === 'function') {
    var c_header = document.querySelector('#header-group') || document.body;
    if (c_header) {
      new MutationObserver(c_init).observe(c_header, { childList: true, subtree: true });
    }
  }

  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(c_init, 0);
  });
})();
